import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import React, { useEffect, useRef, useState } from 'react'

import { useEmulatorStore } from '../../../stores/emulator/store.tsx'
import { defaultLat, defaultLng } from '../../../stores/emulator/types_maps.tsx'
import { defaultMapStyle, roadMapStyle } from './MapStyles.js'
import EmulatorMarkers from './Markers/EmulatorMarkers.jsx'
import { ConnectedEmulatorComponent } from './Trip/ConnectedEmulatorComponent.jsx'
import { PathComponent } from './Trip/PathComponent.jsx'
import { StopComponents } from './Trip/StopComponents.jsx'

const libraries = ['drawing', 'places']

const GoogleMapContainer = () => {
  const mapRef = useRef(null)
  const styleRef = useRef('default')
  const [savedZoom, setSavedZoom] = useState(null)
  const [savedCenter, setSavedCenter] = useState(null)
  const isPrevioiusStateShowed = useRef(false)
  const [mapView, setMapView] = useState('roadmap') // tracks current map style

  const handleMapStateChange = () => {
    if (mapRef.current && isPrevioiusStateShowed.current === true) {
      localStorage.setItem('mapZoom', mapRef.current.getZoom())
      localStorage.setItem(
        'mapCenter',
        JSON.stringify(mapRef.current.getCenter().toJSON())
      )
    }
  }
  // const hasFittedBoundsOnce = useRef(false)
  // const previousEmulatorId = useRef(null)

  // // Pass this as a prop or get from store
  // const selectedEmulatorId = useEmulatorStore((state) => state.selectedEmulator)
  useEffect(
    () =>
      useEmulatorStore.subscribe(
        (state) => state.draggedEmulator, // to show highlighted road map when dragging
        (draggedEmulator) => {
          if (!mapRef.current) return
          // If current view is 'satellite', skip applying styles
          if (styleRef.current === 'satellite') return

          // not-dragged
          if (draggedEmulator && !draggedEmulator?.isDragMarkerDropped) {
            // Dragging started
            if (styleRef.current === 'roadmap') return
            styleRef.current = 'roadmap'
            mapRef.current.setOptions({ styles: roadMapStyle })
          } else if (!draggedEmulator || draggedEmulator?.isDragMarkerDropped) {
            // dragged
            if (styleRef.current === 'default') return
            styleRef.current = 'default'
            mapRef.current.setOptions({ styles: defaultMapStyle })
          }
        }
      ),
    []
  )

  useEffect(
    () =>
      useEmulatorStore.subscribe(
        (state) => state.movedEmulator,
        (movedEmulator) => {
          if (mapRef.current === null || mapRef.current === undefined) {
            return
          }
          // moved
          if (movedEmulator && movedEmulator.moveMarker) {
            mapRef.current?.setCenter({
              lat: movedEmulator.latitude,
              lng: movedEmulator.longitude
            })
            mapRef.current?.setZoom(10)
            // do something maybe
          } else if (!movedEmulator || movedEmulator?.isDragMarkerDropped) {
            // un-moved..
            // do something maybe
          }
        }
      ),
    []
  )

  useEffect(
    () =>
      useEmulatorStore.subscribe(
        (state) => state.center,
        (center) => {
          if (mapRef.current === null || mapRef.current === undefined) {
            return
          }
          // center changed
          if (center && center.lat && center.lng) {
            mapRef.current?.setCenter(center)
          }
        }
      ),
    []
  )

  // useEffect(() => {
  //   // Detect emulator switch
  //   if (selectedEmulatorId !== previousEmulatorId.current) {
  //     hasFittedBoundsOnce.current = false
  //     previousEmulatorId.current = selectedEmulatorId
  //   }
  // }, [selectedEmulatorId])

  useEffect(() => {
    // const unsubscribe = useEmulatorStore.subscribe(
    return useEmulatorStore.subscribe(
      (state) => state.tripData,
      (tripData) => {
        // if (!mapRef.current || hasFittedBoundsOnce.current) return

        // if (tripData?.status === 'rest' || tripData?.status === 'break') {
        const map = mapRef.current
        if (
          !map ||
          !tripData ||
          !tripData.tripPoints ||
          tripData.tripPoints.length === 0
        ) {
          return
        }
        // if (tripData && tripData.tripPoints && tripData.tripPoints.length > 0) {
        //   const bounds = new window.google.maps.LatLngBounds()

        //   const first = tripData.tripPoints[0]
        //   const last = tripData.tripPoints[tripData.tripPoints.length - 1]
        //   const middle =
        //     tripData.tripPoints[Math.floor(tripData.tripPoints.length / 2)]
        const points = tripData.tripPoints
        const bounds = new window.google.maps.LatLngBounds()

        // bounds.extend(new window.google.maps.LatLng(first.lat, first.lng))
        // bounds.extend(new window.google.maps.LatLng(middle.lat, middle.lng))
        // bounds.extend(new window.google.maps.LatLng(last.lat, last.lng))
        bounds.extend(
          new window.google.maps.LatLng(points[0].lat, points[0].lng)
        )
        bounds.extend(
          new window.google.maps.LatLng(
            points[points.length - 1].lat,
            points[points.length - 1].lng
          )
        )
        bounds.extend(
          new window.google.maps.LatLng(
            points[Math.floor(points.length / 2)].lat,
            points[Math.floor(points.length / 2)].lng
          )
        )
        // mapRef.current.fitBounds(bounds)
        const savedZoom = localStorage.getItem('mapZoom')
        const savedCenter = localStorage.getItem('mapCenter')
        // hasFittedBoundsOnce.current = true
        // setPreviousSavedZoomAndCenterIfAvailable()
        if (!savedZoom || !savedCenter || savedCenter === '[object Object]') {
          map.fitBounds(bounds)
        }
        setPreviousSavedZoomAndCenterIfAvailable()
      }
    )

    // return () => unsubscribe()
  }, [])

  const setPreviousSavedZoomAndCenterIfAvailable = () => {
    if (mapRef.current) {
      const savedMapZoom = localStorage.getItem('mapZoom')
      const mapCenter = localStorage.getItem('mapCenter')
      if (typeof mapCenter === 'string' && mapCenter !== '[object Object]') {
        const savedMapCenter = JSON.parse(mapCenter)

        if (savedMapZoom && savedMapCenter) {
          setSavedZoom(Number(savedMapZoom))
          setSavedCenter(savedMapCenter)
        }
      }
      // previous saved state is shown so start saving new zoom and map state.
      isPrevioiusStateShowed.current = true
    }
  }

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_APIS_KEY,
    libraries
  })

  const containerStyle = {
    position: 'unset !important',
    width: '100%',
    height: '100%'
  }

  return isLoaded ? (
    <>
    {/* Satellite Toggle Button */}
    <button
  style={{
    position: 'absolute',
    display: 'block',
    top: 20,
    right: 268,
    zIndex: 2,
    padding: '6px 6px',
    background: 'rgb(44, 111, 235)',
    borderRadius: '4px',
    cursor: 'pointer'
  }}
  onClick={() => {
    if (!mapRef.current) return
    // Preserve current zoom and center before switching view
    const currentZoom = mapRef.current.getZoom()
    const currentCenter = mapRef.current.getCenter()
    if (styleRef.current === 'hybrid') {
      // Switch to default
      styleRef.current = 'roadmap'
      setMapView('roadmap')
      mapRef.current?.setMapTypeId('roadmap')
      mapRef.current?.setOptions({ styles: [] })
    } else {
      // Switch to satellite
      styleRef.current = 'hybrid'
      setMapView('satellite')
      mapRef.current?.setMapTypeId('hybrid')
      mapRef.current?.setOptions({ styles: [] }) // Remove custom styles
    }
    mapRef.current.setZoom(currentZoom)
    mapRef.current.setCenter(currentCenter)
  }}
>View: {mapView}
</button>
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={
        savedCenter !== null
          ? savedCenter
          : { lat: defaultLat, lng: defaultLng }
      }
      zoom={savedZoom !== null ? savedZoom : 4}
      gestureHandling="none"
      zoomControl={false}
      mapTypeId={styleRef.current === 'hybrid' ? 'hybrid' : 'roadmap'}
      options={{
        minZoom: 3,
        maxZoom: 20,
        scrollwheel: true,
        disableDefaultUI: true
        // fullscreenControl: true
        // styles: defaultMapStyle,
      }}
      onLoad={(ref) => {
        mapRef.current = ref
        const mapCenter = localStorage.getItem('mapCenter')
        if (typeof mapCenter === 'string' && mapCenter !== '[object Object]') {
          isPrevioiusStateShowed.current = false
        } else {
          isPrevioiusStateShowed.current = true
        }
        mapRef.current.addListener('zoom_changed', handleMapStateChange)
        mapRef.current.addListener('center_changed', handleMapStateChange)
      }}
      onUnmount={() => {
        mapRef.current = null
      }}
    >
      <PathComponent mapRef={mapRef} />
      <ConnectedEmulatorComponent />
      <StopComponents />
      <EmulatorMarkers />
    </GoogleMap>
    </>
  ) : (
  <>Loading...</>
  )
}

export default GoogleMapContainer
